'use client';

import React, { useState, useEffect } from 'react';
import { Button, GlobalLoading, Input, Modal } from '@/shared/components';
import { useAppStore } from '@/store/app-store';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { useSendEmail } from '@/hooks/cart.hook';
import { useProductStore } from '../product-store';
import { useCartStore } from '../cart-store';
import { Product } from '@/types/cart';
import { usePriceStore } from '@/store/price-store';
import { getPrice } from '@/app/utils/get-price';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
}
export const EmailPopUp = (props: IProps) => {
  const [sendEnable, setSendEnable] = useState<boolean>();
  const [emailAddress, setEmailAddress] = useState<string>('');
  const [emailList, setEmailList] = useState<any>([]);
  const { t } = useLang(NAMESPACE.cart);
  const { t: commonT } = useLang(NAMESPACE.common);
  const { userInfo, isAnonymous } = useAppStore();
  const emailLimit = 5;
  const senEmailApi = useSendEmail();
  const { cartId, availabilityByCatalogNumber } = useCartStore();
  const { products } = useProductStore();
  const { productPrice } = usePriceStore();

  useEffect(() => {
    isAnonymous ? setSendEnable(true) : setSendEnable(false);
  }, [isAnonymous]);

  useEffect(() => {
    emailList.length > 0 || !isAnonymous ? setSendEnable(false) : setSendEnable(true);
  }, [emailList]);

  function cartValidateEmail(email: any, sep: string = ',') {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    sep = email?.includes(';') ? ';' : sep;
    try {
      if (email?.includes(sep)) {
        const emails = String(email)?.toLowerCase()?.split(sep);
        return !emails
          ?.reduce((a, b) => {
            const e = b.trim();
            a.push(re.test(e));
            return a;
          }, [])
          .includes(false);
      } else {
        return re.test(String(email).toLowerCase());
      }
    } catch (error) {
      return false;
    }
  }
  function removeEmailList(email) {
    setEmailList(emailList.filter((item) => item !== email));
  }
  function updateEmailList() {
    const tempList = [...emailList];
    tempList.push(emailAddress);
    setEmailList(tempList);
    setEmailAddress('');
    setSendEnable(true);
  }

  function generateProducts(products: Product[]) {
    return products.map((product) => {
      const price = productPrice[product.catalogNumber];
      const availability = availabilityByCatalogNumber[product.catalogNumber];
      const priceInfo = getPrice(product, availability, price, commonT);
      if (typeof priceInfo === 'string') {
        return {
          ...product,
          contactUs: commonT('contact.us'),
        };
      } else {
        return {
          ...product,
          bestPrice: priceInfo.bestPrice,
        };
      }
    });
  }

  function sendEmail() {
    if (!products.length || (!emailList.length && !userInfo.emailAddress)) return;
    const emails: string[] = Array.isArray(emailList) ? [...emailList] : [];
    if (userInfo.emailAddress) {
      emails.push(userInfo.emailAddress);
    }
    GlobalLoading.show();
    props.onClose();
    const formatProducts = generateProducts(products);
    senEmailApi
      .mutateAsync({
        cartId,
        products: formatProducts,
        emails,
      })
      .finally(() => {
        GlobalLoading.hide();
      });
  }

  return (
    <Modal
      title={<span className="sm:text-xl">{t('enter.email')}</span>}
      size="medium"
      isOpen={props.isOpen}
      handleClose={props.onClose}
      handleCancel={props.onClose}
      saveButtonLabel={t('send')}
      cancelButtonLabel={t('cancel')}
      disableSaveButton={sendEnable}
      handleSave={sendEmail}
    >
      <div className="flex mt-2 sm:flex-col sm:flex-1 sm:px-1 sm:mt-0 ">
        <Input
          className="flex mr-2 w-[590px] sm:w-full sm:mr-0 sm:min-w-full sm:block"
          placeholder="Email address"
          value={emailAddress}
          onChange={(e) => {
            setEmailAddress(e.target.value);
          }}
        />
        <Button
          className="flex sm:mt-2 sm:items-center sm:justify-center"
          label={t('add')}
          disabled={!cartValidateEmail(emailAddress) || emailList.length === emailLimit}
          onClick={updateEmailList}
        />
      </div>
      <div className="mt-2">
        {!isAnonymous && (
          <div className="flex justify-between p-4">
            <p>
              {userInfo.emailAddress} ({t('you')})
            </p>
          </div>
        )}
        {emailList && emailList.length > 0 && (
          <div className="mt-2 shadow-inner">
            {emailList.map((email, key) => {
              return (
                <div className="flex justify-between p-4" key={key}>
                  <p className="self-center">{email}</p>
                  <span className="text-2xl" onClick={() => removeEmailList(email)}>
                    &times;
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div>{t('send.mail')}</div>
    </Modal>
  );
};
